/**
 * SocialGraphContext — client-side view of the social graph (Phase B).
 *
 * Holds inbound/outbound connection requests and accepted connections, and
 * exposes the graph actions (send/accept/decline/cancel/mute). Block/unblock
 * stay on `safetyService`; this context reflects their effect on next refresh
 * (and, once the presence hub is wired, live via ConnectionStateChanged).
 *
 * Everything here is inert until `social.graph` is on — the provider mounts
 * app-wide but only fetches when the flag is enabled and a user is present.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useUser } from './UserContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { socialService } from '../services/api/socialService';
import { getBlockedUsers } from '../services/api/safetyService';
import type {
  Connection,
  ConnectionRequest,
  ConnectionState,
} from '../types/social';

interface SocialGraphState {
  inbound: ConnectionRequest[];
  outbound: ConnectionRequest[];
  connections: Connection[];
  /**
   * Profile ids the viewer has blocked. Block severs the graph edge, so a
   * blocked pair is NOT in `connections` (see types/social.ts) — the block
   * store (safetyService) is the only local source of 'blocked'.
   */
  blockedIds: string[];
  isLoading: boolean;
  error: string | null;
}

interface SocialGraphContextValue extends SocialGraphState {
  refresh: () => Promise<void>;
  sendRequest: (targetProfileId: string) => Promise<void>;
  accept: (requestId: string) => Promise<void>;
  decline: (requestId: string) => Promise<void>;
  cancel: (requestId: string) => Promise<void>;
  setMuted: (connectionId: string, muted: boolean) => Promise<void>;
  /** Local best-effort state for a profile, derived from loaded graph data. */
  stateFor: (profileId: string) => ConnectionState;
}

const SocialGraphContext = createContext<SocialGraphContextValue | null>(null);

const EMPTY: SocialGraphState = {
  inbound: [],
  outbound: [],
  connections: [],
  blockedIds: [],
  isLoading: false,
  error: null,
};

interface ProviderProps {
  children: ReactNode;
}

export function SocialGraphProvider({ children }: ProviderProps) {
  const { user } = useUser();
  const enabled = useFeatureFlag('social.graph');
  const [state, setState] = useState<SocialGraphState>(EMPTY);

  const refresh = useCallback(async () => {
    if (!enabled || !user?.id) {
      setState(EMPTY);
      return;
    }
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const [inbound, outbound, connections, blocked] = await Promise.all([
        socialService.getInboundRequests(),
        socialService.getOutboundRequests(),
        socialService.getConnections(),
        getBlockedUsers(),
      ]);
      setState({
        inbound,
        outbound,
        connections,
        blockedIds: blocked.map((b) => b.profileId),
        isLoading: false,
        error: null,
      });
    } catch (e) {
      setState((s) => ({
        ...s,
        isLoading: false,
        error: e instanceof Error ? e.message : 'Failed to load connections',
      }));
    }
  }, [enabled, user?.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const sendRequest = useCallback(
    async (targetProfileId: string) => {
      await socialService.sendConnectionRequest(targetProfileId);
      await refresh();
    },
    [refresh]
  );

  const accept = useCallback(
    async (requestId: string) => {
      await socialService.acceptRequest(requestId);
      await refresh();
    },
    [refresh]
  );

  const decline = useCallback(
    async (requestId: string) => {
      await socialService.declineRequest(requestId);
      await refresh();
    },
    [refresh]
  );

  const cancel = useCallback(
    async (requestId: string) => {
      await socialService.cancelRequest(requestId);
      await refresh();
    },
    [refresh]
  );

  const setMuted = useCallback(
    async (connectionId: string, muted: boolean) => {
      if (muted) await socialService.muteConnection(connectionId);
      else await socialService.unmuteConnection(connectionId);
      setState((s) => ({
        ...s,
        connections: s.connections.map((c) =>
          c.id === connectionId ? { ...c, muted } : c
        ),
      }));
    },
    []
  );

  const stateFor = useCallback(
    (profileId: string): ConnectionState => {
      // Block is authoritative and read from the block store, not the graph —
      // a blocked pair has no connection edge, so this must be checked against
      // blockedIds (never against connections, which can never hold 'blocked').
      // Checked first so a blocked profile never falls through to a Connect action.
      if (state.blockedIds.includes(profileId)) return 'blocked';
      if (state.connections.some((c) => c.otherProfileId === profileId)) return 'accepted';
      if (state.outbound.some((r) => r.targetProfileId === profileId && r.status === 'pending'))
        return 'pending_outbound';
      if (state.inbound.some((r) => r.initiatorProfileId === profileId && r.status === 'pending'))
        return 'pending_inbound';
      return 'none';
    },
    [state.blockedIds, state.connections, state.outbound, state.inbound]
  );

  const value = useMemo<SocialGraphContextValue>(
    () => ({ ...state, refresh, sendRequest, accept, decline, cancel, setMuted, stateFor }),
    [state, refresh, sendRequest, accept, decline, cancel, setMuted, stateFor]
  );

  return <SocialGraphContext.Provider value={value}>{children}</SocialGraphContext.Provider>;
}

export function useSocialGraph(): SocialGraphContextValue {
  const ctx = useContext(SocialGraphContext);
  if (!ctx) throw new Error('useSocialGraph must be used within a SocialGraphProvider');
  return ctx;
}
